import React, { useState, useEffect } from 'react';
import ekybService from '../../services/ekybService';
import '../../styles/ekyb.css';

const StepAML = ({ entityData, uboData, onScreeningComplete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [screeningResult, setScreeningResult] = useState(null);

  useEffect(() => {
    performScreening();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performScreening = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('StepAML: Starting AML screening', { entityData, uboData });

      if (!entityData || !entityData.name) {
        throw new Error('Company data is missing. Please go back and search again.');
      }

      if (!uboData || uboData.length === 0) {
        throw new Error('UBO data is missing. Please go back and add UBOs.');
      }

      const result = await ekybService.performAMLScreening({
        entityName: entityData.name,
        ubos: uboData.map(ubo => ({
          name: ubo.name,
          dob: ubo.dob,
          nationality: ubo.nationality,
          ownership: parseFloat(ubo.ownership)
        }))
      });

      console.log('StepAML: Screening successful', result);

      // Map n8n response format to component format
      const mappedResult = {
        ...result,
        overallRisk: result.overallRisk?.rating || result.overallRisk || 'LOW',
        entityScreening: result.companyScreening || result.entityScreening,
        uboScreening: result.uboResults || result.uboScreening
      };

      setScreeningResult(mappedResult);
    } catch (err) {
      console.error('StepAML: Screening failed', err);
      setError(err.message || 'AML screening failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadgeClass = (risk) => {
    if (!risk) return 'low';
    const riskLower = risk.toLowerCase();
    if (riskLower === 'low') return 'low';
    if (riskLower === 'medium') return 'medium';
    if (riskLower === 'high' || riskLower === 'reject') return 'high';
    return 'low';
  };

  const getUBOStatusBadge = (status) => {
    if (!status) return 'low';
    const statusLower = status.toLowerCase();
    if (statusLower === 'clear' || statusLower === 'no_match') return 'low';
    if (statusLower === 'review_required') return 'medium';
    if (statusLower === 'match_found') return 'high';
    return 'low';
  };

  const handleContinue = () => {
    onScreeningComplete(screeningResult);
  };

  if (loading) {
    return (
      <div className="ekyb-step-container">
        <div className="ekyb-loading">
          <div className="ekyb-spinner"></div>
          <span style={{ marginLeft: '12px' }}>Performing AML screening via OpenSanctions...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ekyb-step-container">
        <div className="ekyb-alert error">
          {error}
        </div>
        <div className="ekyb-button-group">
          <button
            className="ekyb-button ekyb-button-primary"
            onClick={performScreening}
          >
            Retry Screening
          </button>
        </div>
      </div>
    );
  }

  if (!screeningResult) {
    return (
      <div className="ekyb-step-container">
        <div className="ekyb-alert error">
          No screening results available. Please retry or go back.
        </div>
        <div className="ekyb-button-group">
          <button
            className="ekyb-button ekyb-button-primary"
            onClick={performScreening}
          >
            Retry Screening
          </button>
        </div>
      </div>
    );
  }

  const requiresManualReview = screeningResult.overallRisk === 'MEDIUM' || screeningResult.overallRisk === 'HIGH' || screeningResult.overallRisk === 'REJECT';

  return (
    <div className="ekyb-step-container">
      <div className="ekyb-step-header">
        <h2 className="ekyb-step-title">AML Screening Results</h2>
        <p className="ekyb-step-subtitle">
          Screening completed against OpenSanctions database (sanctions, PEP, adverse media).
        </p>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <span className={`ekyb-status-badge ${getRiskBadgeClass(screeningResult.overallRisk)}`}>
          {screeningResult.overallRisk || 'LOW RISK'}
        </span>
      </div>

      {requiresManualReview && (
        <div className="ekyb-alert warning">
          <strong>Manual Review Required</strong><br />
          This verification requires manual compliance review due to {screeningResult.overallRisk} risk rating. Please contact your compliance team.
        </div>
      )}

      {screeningResult.entityScreening && (
        <div style={{ marginBottom: '24px' }}>
          <h3 className="ekyb-step-subtitle" style={{ fontWeight: 600, marginBottom: '12px' }}>
            Entity Screening
          </h3>
          <table className="ekyb-data-table">
            <tbody>
              <tr>
                <td>Entity Name</td>
                <td>{screeningResult.entityScreening.name || entityData.name}</td>
              </tr>
              <tr>
                <td>Screening Status</td>
                <td>
                  <span className={`ekyb-status-badge ${getUBOStatusBadge(screeningResult.entityScreening.status)}`}>
                    {screeningResult.entityScreening.status || 'CLEAR'}
                  </span>
                </td>
              </tr>
              {screeningResult.entityScreening.matches > 0 && (
                <tr>
                  <td>Matches Found</td>
                  <td>{screeningResult.entityScreening.matches}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <h3 className="ekyb-step-subtitle" style={{ fontWeight: 600, marginBottom: '12px' }}>
          UBO Screening Results
        </h3>
        <table className="ekyb-ubo-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Flags</th>
            </tr>
          </thead>
          <tbody>
            {screeningResult.uboScreening && screeningResult.uboScreening.map((ubo, index) => (
              <tr key={index}>
                <td style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{ubo.name}</td>
                <td>
                  <span className={`ekyb-status-badge ${getUBOStatusBadge(ubo.status)}`}>
                    {ubo.status || 'CLEAR'}
                  </span>
                </td>
                <td>
                  {ubo.flags && ubo.flags.length > 0 ? (
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {ubo.flags.map((flag, i) => (
                        <span
                          key={i}
                          className={`ekyb-status-badge ${flag === 'SANCTIONED' ? 'high' : flag === 'PEP' ? 'medium' : 'low'}`}
                          style={{ fontSize: '10px', padding: '4px 8px' }}
                        >
                          {flag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--ekyb-text-tertiary)', fontSize: '12px' }}>No flags</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ekyb-button-group">
        <button
          className="ekyb-button ekyb-button-primary"
          onClick={handleContinue}
        >
          Continue to MiCA Check
        </button>
      </div>
    </div>
  );
};

export default StepAML;
